/**
 * Hover (tooltip) text with more precision than the cell shows, for the Risk
 * Limits pages:
 *  - a figure displayed with 2 decimals (millions, "$12.35M"; "2.18%") hovers to up to 4
 *  - anything else (whole dollars, 0 or 1 decimal percents) hovers to up to 2
 * Negatives keep the page's convention - in brackets, e.g. ($1.2346M).
 */

const nf = (min, max) => new Intl.NumberFormat("en-US", { minimumFractionDigits: min, maximumFractionDigits: max });

const isMissing = (value) => value === null || value === undefined || value === "" || Number.isNaN(Number(value));

/** Dollar amount: millions are shown with 2 decimals, so up to 4; whole dollars up to 2. Null when missing. */
export function exactExposure(value) {
  if (isMissing(value)) return null;
  const v = Number(value);
  const abs = Math.abs(v);
  const body = abs >= 1e6 ? `$${nf(2, 4).format(abs / 1e6)}M` : `$${nf(0, 2).format(abs)}`;
  return v < 0 && abs > 0 ? `(${body})` : body;
}

/** SMV is reported as a positive magnitude but displayed as a negative (short) exposure. */
export function exactShortExposure(value) {
  return isMissing(value) ? null : exactExposure(-Math.abs(Number(value)));
}

/**
 * Percent value (already on a 0-100 scale) given how many decimals the cell
 * shows: 2 or more -> up to 4, fewer -> up to 2.
 */
export function exactPercent(value, shownDecimals = 1) {
  if (isMissing(value)) return null;
  const v = Number(value);
  const [min, max] = shownDecimals >= 2 ? [2, 4] : [shownDecimals, 2];
  const body = `${nf(min, max).format(Math.abs(v))}%`;
  return v < 0 && Math.abs(v) > 0 ? `(${body})` : body;
}

/** A percentage-point delta shown with 1 decimal -> up to 2. */
export function exactPoints(value, shownDecimals = 1) {
  return isMissing(value) ? null : nf(shownDecimals, shownDecimals >= 2 ? 4 : 2).format(Math.abs(Number(value)));
}
